import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapBanner } from '@/components/map-banner';
import { ProductSection } from '@/components/product-section';
import { CategoryNav } from '@/components/category-nav';
import { SubsectionHeader } from '@/components/section-header';
import { FloatingRestaurantButton } from '@/components/floating-restaurant-button';
import { Clock, MapPin, Phone } from 'lucide-react';
import type { Product, SystemSettings } from '@shared/schema';

export default function Home() {
  // Set page title
  useEffect(() => {
    document.title = 'Tops100 Supermarket - Fresh Products Daily';
  }, []);
  const { data: allProducts } = useQuery<Product[]>({
    queryKey: ['/api/products'],
    refetchInterval: 30000,
  });

  const { data: systemSettings } = useQuery<SystemSettings>({
    queryKey: ['/api/admin/settings'],
    refetchInterval: 30000,
  });

  const formattedDate = systemSettings?.lastUpdated
    ? new Date(systemSettings.lastUpdated).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    : new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Constrained Container */}
      <div className="relative max-w-[1400px] mx-auto bg-white shadow-sm">
        {/* Map Banner */}
        <MapBanner />

        {/* Price Update Header */}
        <div className="border-b border-gray-100 bg-white">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Today's Prices
            </h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Updated: {formattedDate}</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <CategoryNav />

        {/* Product Sections */}

        {/* FRESH PRODUCE */}
        <ProductSection
          category="produce"
          title="Fresh Produce"
          chineseTitle="新鮮蔬果"
          bgColor="bg-emerald-50"
          useRowLayout={false}
          compact={false}
          columns={4}
          accentColor="emerald"
        />

        {/* MEAT Section with Subcategories */}
        <section id="meat-section" className="relative py-6 bg-red-50 overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Main Meat Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-red-500 rounded-full" />
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">Meat</h2>
                <span className="text-lg text-gray-500 font-normal">肉類</span>
              </div>
            </div>

            {/* Beef Subcategory */}
            <div className="mb-8">
              <SubsectionHeader title="Beef" chineseTitle="牛肉" />
              <ProductSection
                category="meat-beef"
                title=""
                bgColor="transparent"
                useRowLayout={false}
                compact={true}
                columns={3}
                accentColor="red"
              />
            </div>

            {/* Chicken Subcategory */}
            <div className="mb-8">
              <SubsectionHeader title="Chicken" chineseTitle="雞肉" />
              <ProductSection
                category="meat-chicken"
                title=""
                bgColor="transparent"
                useRowLayout={false}
                compact={true}
                columns={3}
                accentColor="red"
              />
            </div>

            {/* Pork Subcategory */}
            <div className="mb-8">
              <SubsectionHeader title="Pork" chineseTitle="豬肉" />
              <ProductSection
                category="meat-pork"
                title=""
                bgColor="transparent"
                useRowLayout={false}
                compact={true}
                columns={3}
                accentColor="red"
              />
            </div>

            {/* Hot Pot Subcategory */}
            <div>
              <SubsectionHeader title="Hot Pot" chineseTitle="火鍋肉類" />
              <ProductSection
                category="meat-hotpot"
                title=""
                bgColor="transparent"
                useRowLayout={false}
                compact={true}
                columns={3}
                accentColor="red"
              />
            </div>
          </div>
        </section>

        {/* SEAFOOD */}
        <ProductSection
          category="seafood"
          title="Seafood"
          chineseTitle="海鮮"
          bgColor="bg-blue-50"
          useRowLayout={false}
          compact={false}
          columns={4}
          accentColor="blue"
        />

        {/* GROCERIES */}
        <ProductSection
          category="groceries"
          title="Asian Groceries"
          chineseTitle="亞洲雜貨"
          bgColor="bg-amber-50"
          useRowLayout={false}
          compact={false}
          columns={4}
          accentColor="amber"
        />

        {/* CTA Section */}
        <section className="py-16 bg-emerald-600">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-2">
              Wholesale Orders
            </h2>
            <p className="text-emerald-100 mb-8">
              Minimum order required
            </p>
            <a
              href="/wholesaleorderform.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-emerald-700 px-8 py-3 rounded-lg font-medium hover:bg-emerald-50 transition-colors shadow-lg"
            >
              <Phone className="w-5 h-5" />
              Contact for Wholesale
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-50 border-t border-gray-200 py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Logo */}
              <div>
                <img
                  src="/images/TopsLogoCrop.png"
                  alt="Tops Supermarket"
                  className="h-16"
                />
              </div>

              {/* Spacer */}
              <div />

              {/* Contact Info */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-emerald-600" />
                    Location
                  </h4>
                  <p className="text-gray-600">1623 Centre St NW, Calgary AB</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-emerald-600" />
                    Hours
                  </h4>
                  <p className="text-gray-600">Mon-Sun: 9AM - 6PM</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 mt-8 pt-8 text-center">
              <p className="text-gray-500 text-sm">
                &copy; 2025 Tops Supermarket. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Floating Action Button - Outside container */}
      <FloatingRestaurantButton />
    </div>
  );
}
